import type { StorageCondition, StorageConditionMeta } from '@/types/storage'

/**
 * Which locations may hold a given material — the client's copy of the rule the
 * server enforces on put-away.
 *
 * Mirrors `checkPutAway` in `cgt-backend/src/inventory/storage/storage-conditions.ts`,
 * including its wording. It exists so a destination that would be REFUSED is
 * never offered in the first place: before this, the picker listed every unit,
 * the operator chose one, filled the form, and learned at submit that the
 * material cannot be kept there.
 *
 * This narrows the UI. It does NOT replace the server check — a stale client,
 * a direct API call, or a condition changed between load and submit all still
 * have to be refused, and are.
 *
 * Pure and dependency-free: this repo's vitest only collects `src/**\/*.test.ts`,
 * so a rule left inside the .tsx would have no coverage at all.
 */

export type PutAwayCheck = { ok: true } | { ok: false; reason: string }

/** A location, as far as this rule is concerned. */
export interface ConditionBearing {
  providesCondition: StorageCondition | null
}

function metaOf(
  condition: StorageCondition,
  conditions: StorageConditionMeta[],
): StorageConditionMeta | null {
  return conditions.find((c) => c.name === condition) ?? null
}

/** "Vapor-phase LN₂ (≤ −150 °C)", or the raw enum if the reference has not loaded. */
export function conditionLabel(
  condition: StorageCondition,
  conditions: StorageConditionMeta[],
): string {
  const meta = metaOf(condition, conditions)
  return meta ? `${meta.label} (${meta.temperature})` : condition
}

/**
 * Compare what a material needs against what a location provides.
 *
 * Matching is EXACT, including within a temperature family. Vapour phase and
 * immersion are both cryogenic LN₂ and immersion is colder, but it is not a
 * substitute: the cross-contamination risk has to be assessed rather than
 * assumed away, so the model refuses and leaves the judgement to a person.
 *
 * A material that states no requirement passes anywhere. Refusing there would
 * block ordinary consumables on missing master data, which teaches people to
 * ignore the check.
 *
 * `conditions` is the reference table from `GET /inventory/storage/conditions`.
 * While it is still loading it arrives empty, and only the `persistent` rule
 * depends on it — an unknown condition is treated as a place stock can rest,
 * which at worst offers one destination the server then refuses, rather than
 * hiding every unit behind a spinner.
 */
export function checkPutAway(
  required: StorageCondition | null,
  provided: StorageCondition | null,
  conditions: StorageConditionMeta[],
): PutAwayCheck {
  if (required == null) return { ok: true }

  if (provided == null) {
    return {
      ok: false,
      reason: `No storage condition set, and ${conditionLabel(required, conditions)} is required.`,
    }
  }

  if (metaOf(provided, conditions)?.persistent === false) {
    return {
      ok: false,
      reason: `A ${
        // `?.` on the label too, not only on the lookup. The fallback was
        // written for a row that arrives without one, but `?.label.toLowerCase()`
        // only guards the LOOKUP being absent — a row that exists with no label
        // threw here instead of falling back, taking put-away validation down
        // with it.
        metaOf(provided, conditions)?.label?.toLowerCase() ?? provided
      } is not a storage location — product passes through one, it is not kept there.`,
    }
  }

  if (required === provided) return { ok: true }

  return {
    ok: false,
    reason: `Needs ${conditionLabel(required, conditions)}; this location is ${conditionLabel(
      provided,
      conditions,
    )}.`,
  }
}

export interface ConditionPartition<T> {
  /** Offer these. */
  eligible: T[]
  /** Kept out of the picker, each with the reason it cannot take this material. */
  hidden: { unit: T; reason: string }[]
}

/**
 * Split locations into what may hold this material and what may not.
 *
 * Generic over the node shape so both destination pickers — Receive stock and
 * Set location — narrow by the same rule rather than each growing its own.
 *
 * The hidden ones are returned rather than dropped: a picker that silently
 * shows three of a facility's nine freezers reads as broken data. The count,
 * and why, is what the caller states above the field.
 */
export function partitionByCondition<T extends ConditionBearing>(
  units: T[],
  required: StorageCondition | null,
  conditions: StorageConditionMeta[],
): ConditionPartition<T> {
  if (required == null) return { eligible: units, hidden: [] }

  const eligible: T[] = []
  const hidden: { unit: T; reason: string }[] = []
  for (const unit of units) {
    const check = checkPutAway(required, unit.providesCondition, conditions)
    if (check.ok) eligible.push(unit)
    else hidden.push({ unit, reason: check.reason })
  }
  return { eligible, hidden }
}

/**
 * The line above the picker saying what it is showing, or null when nothing was
 * narrowed and there is nothing to explain.
 *
 * Names the number kept out as well as the requirement. "Only units operated at
 * X" alone still leaves someone hunting for the freezer they can see across the
 * room; "· 3 hidden" tells them the screen knows about it and has a reason.
 */
export function describeNarrowing(
  required: StorageCondition | null,
  hiddenCount: number,
  conditions: StorageConditionMeta[],
): string | null {
  if (required == null || hiddenCount === 0) return null
  return `Only units operated at ${conditionLabel(required, conditions)} · ${hiddenCount} hidden`
}
